import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import {
  LuArrowLeft, LuCheckCircle2, LuCircle, LuLock, LuListChecks, LuFileDown, LuChevronLeft, LuChevronRight, LuAward, LuPlay, LuClock, LuList, LuXCircle,
} from 'react-icons/lu';
import TrainingAttemptView from './TrainingAttemptView';
import { downloadCertificate } from './trainingCertificate';
import {
  ENROLLMENT_STATUS, Badge, errorMessage, embedUrl, downloadFile, fmtDate, fmtScore,
} from './trainingUtils';

/**
 * Sala de aula do colaborador: navegação por módulos (aulas + avaliações),
 * conteúdo da aula e painel de cada avaliação. Bloqueios (conclusão
 * sequencial), progresso e aprovação vêm prontos do backend
 * (GET /my-training/courses/:id) — a tela só exibe.
 */

const itemKey = (kind, id) => `${kind}:${id}`;

function LessonContent({ lesson, course, onComplete, completing }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const video = ['video', 'video_text'].includes(lesson.content_type) ? embedUrl(lesson.video_url) : null;
  const canComplete = !course.read_only && course.enrollment?.status !== 'approved' && course.enrollment?.status !== 'failed';

  const handleMaterial = async () => {
    setDownloading(true); setError('');
    try {
      const { data } = await api.get(`/my-training/lessons/${lesson.id}/material`);
      downloadFile(data.url, data.name);
    } catch (e) {
      setError(errorMessage(e, 'Não foi possível baixar o material'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div className="training-player-heading">
        <h2>{lesson.title}</h2>
        <div className="training-row-meta">
          <span>{lesson.required ? 'Aula obrigatória' : 'Aula opcional'}</span>
          {lesson.video_duration_minutes ? <span><LuClock /> {lesson.video_duration_minutes} min</span> : null}
          {lesson.completed && <span style={{ color: 'var(--success)', fontWeight: 600 }}><LuCheckCircle2 /> Concluída em {fmtDate(lesson.completed_at)}</span>}
        </div>
      </div>
      {error && <div className="alert-custom danger">{error}</div>}
      {video && (
        <div className="training-video">
          {video.type === 'iframe'
            ? <iframe src={video.src} title={lesson.title} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
            : <video src={video.src} controls playsInline />}
        </div>
      )}
      {lesson.content_html && (
        // HTML já sanitizado no backend (sanitize-html) antes de ser gravado.
        // eslint-disable-next-line react/no-danger
        <div className="training-rich" dangerouslySetInnerHTML={{ __html: lesson.content_html }} />
      )}
      {lesson.has_material && (
        <button type="button" className="training-btn" style={{ marginTop: 14 }} onClick={handleMaterial} disabled={downloading}>
          <LuFileDown /> {downloading ? 'Baixando…' : `Material de apoio: ${lesson.material_name}`}
        </button>
      )}
      {canComplete && !lesson.completed && (
        <div style={{ marginTop: 20 }}>
          <button type="button" className="training-btn training-btn--primary" onClick={onComplete} disabled={completing}>
            <LuCheckCircle2 /> {completing ? 'Registrando…' : 'Marcar aula como concluída'}
          </button>
        </div>
      )}
    </div>
  );
}

function AssessmentPanel({ assessment, course, onStart, starting }) {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!course.enrollment) { setHistory([]); return undefined; }
    let cancelled = false;
    api.get(`/my-training/assessments/${assessment.id}/attempts`)
      .then(({ data }) => { if (!cancelled) setHistory(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [assessment.id, assessment.attempts_used, course.enrollment]);

  const active = !course.read_only && (!course.enrollment || course.enrollment.status === 'in_progress');
  const noAttempts = assessment.attempts_left === 0;
  const unavailable = assessment.question_count === 0;
  const canStart = active && assessment.unlocked && !noAttempts && !unavailable;

  let blocker = null;
  if (!assessment.unlocked) blocker = 'Conclua as etapas anteriores do curso para liberar esta avaliação.';
  else if (unavailable) blocker = 'Esta avaliação ainda não está disponível. Avise o responsável pelo curso.';
  else if (noAttempts) blocker = 'Você usou todas as tentativas desta avaliação.';
  else if (!active) blocker = course.read_only ? 'Curso arquivado: disponível apenas para consulta.' : 'Este curso já foi encerrado para você.';

  return (
    <div>
      <div className="training-player-heading">
        <h2><LuListChecks color="var(--accent)" /> {assessment.title}</h2>
        <div className="training-row-meta"><span>{assessment.module_id ? 'Avaliação do módulo' : 'Prova final do curso'}</span></div>
      </div>
      {assessment.description && <p style={{ fontSize: 14, color: 'var(--text-2)' }}>{assessment.description}</p>}

      <div className="training-kpis">
        <div className="training-kpi"><div className="training-kpi-label">Questões</div><div className="training-kpi-value">{assessment.question_count}</div></div>
        <div className="training-kpi"><div className="training-kpi-label">Nota mínima</div><div className="training-kpi-value">{assessment.passing_score}</div></div>
        <div className="training-kpi"><div className="training-kpi-label">Tempo</div><div className="training-kpi-value">{assessment.time_limit_minutes ? `${assessment.time_limit_minutes} min` : 'Livre'}</div></div>
        <div className="training-kpi">
          <div className="training-kpi-label">Tentativas</div>
          <div className="training-kpi-value">{assessment.attempts_used}{assessment.max_attempts ? ` / ${assessment.max_attempts}` : ''}</div>
        </div>
        <div className="training-kpi">
          <div className="training-kpi-label">Melhor nota</div>
          <div className="training-kpi-value" style={{ color: assessment.passed ? 'var(--success)' : undefined }}>
            {fmtScore(assessment.best_score)}
            {assessment.passed && <LuCheckCircle2 size={16} />}
          </div>
        </div>
      </div>

      {assessment.time_limit_minutes && canStart && !assessment.in_progress_attempt_id && (
        <div className="alert-custom warning"><LuClock /> Ao iniciar, o cronômetro de {assessment.time_limit_minutes} minutos começa a contar e não pausa — mesmo se você sair da tela.</div>
      )}
      {blocker && <div className="alert-custom warning">{blocker}</div>}

      {canStart && (
        <button type="button" className="training-btn training-btn--primary" onClick={onStart} disabled={starting}>
          <LuPlay /> {starting ? 'Abrindo…' : assessment.in_progress_attempt_id ? 'Continuar tentativa' : assessment.attempts_used ? 'Fazer nova tentativa' : 'Iniciar avaliação'}
        </button>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div className="form-section-title">Suas tentativas</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="training-table">
              <thead><tr><th>#</th><th>Enviada em</th><th>Nota</th><th>Resultado</th><th /></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td>{h.attempt_number}</td>
                    <td>{h.submitted_at ? new Date(h.submitted_at).toLocaleString('pt-BR') : 'Em andamento'}</td>
                    <td style={{ fontWeight: 700 }}>{fmtScore(h.score)}</td>
                    <td>
                      {h.status === 'in_progress' && <span className="training-hint" style={{ marginTop: 0 }}>Em andamento</span>}
                      {h.status !== 'in_progress' && (h.passed
                        ? <span style={{ color: 'var(--success)', fontWeight: 600 }}><LuCheckCircle2 /> Aprovado</span>
                        : <span style={{ color: 'var(--danger)', fontWeight: 600 }}><LuXCircle /> Reprovado{h.status === 'expired' ? ' (tempo esgotado)' : ''}</span>)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {h.status !== 'in_progress' && (
                        <button type="button" className="training-btn training-btn--sm" onClick={() => onStart(h.id)}>Ver resultado</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MyCoursePlayer({ courseId, onBack }) {
  const [course, setCourse] = useState(null);
  const [selected, setSelected] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [navOpen, setNavOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/my-training/courses/${courseId}`);
      setCourse(data);
      return data;
    } catch (e) {
      setError(errorMessage(e, 'Erro ao carregar o curso'));
      return null;
    }
  }, [courseId]);

  // Ordem de estudo (mesma do backend): aulas e avaliações de cada módulo, depois prova final.
  const items = useMemo(() => {
    if (!course) return [];
    const list = [];
    course.modules.forEach((m) => {
      m.lessons.forEach((l) => list.push({ kind: 'lesson', id: l.id, data: l, module: m }));
      m.assessments.forEach((a) => list.push({ kind: 'assessment', id: a.id, data: a, module: m }));
    });
    course.final_assessments.forEach((a) => list.push({ kind: 'assessment', id: a.id, data: a, module: null }));
    return list;
  }, [course]);

  useEffect(() => {
    load().then((data) => {
      if (!data) return;
      // Abre no primeiro item liberado e ainda não concluído (ou no primeiro item do curso).
      const flat = [];
      data.modules.forEach((m) => {
        m.lessons.forEach((l) => flat.push({ kind: 'lesson', id: l.id, done: l.completed, unlocked: l.unlocked }));
        m.assessments.forEach((a) => flat.push({ kind: 'assessment', id: a.id, done: a.passed, unlocked: a.unlocked }));
      });
      data.final_assessments.forEach((a) => flat.push({ kind: 'assessment', id: a.id, done: a.passed, unlocked: a.unlocked }));
      const next = flat.find((i) => i.unlocked && !i.done) || flat[0];
      if (next) setSelected(itemKey(next.kind, next.id));
    });
  }, [load]);

  const index = items.findIndex((i) => itemKey(i.kind, i.id) === selected);
  const current = index >= 0 ? items[index] : null;

  const go = (i) => {
    setSelected(itemKey(i.kind, i.id));
    setNavOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStartCourse = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/my-training/courses/${courseId}/enroll`);
      setCourse(data);
    } catch (e) {
      setError(errorMessage(e, 'Não foi possível iniciar o curso'));
    } finally {
      setBusy(false);
    }
  };

  const handleComplete = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/my-training/lessons/${current.id}/complete`);
      setCourse(data);
      // Avança para o próximo item, se houver.
      const after = items[index + 1];
      if (after) go(after);
    } catch (e) {
      setError(errorMessage(e, 'Não foi possível registrar a conclusão'));
    } finally {
      setBusy(false);
    }
  };

  // Inicia/retoma uma tentativa — ou abre o resultado de uma já encerrada (id informado).
  const handleStartAttempt = async (existingAttemptId) => {
    if (typeof existingAttemptId === 'number') { setAttemptId(existingAttemptId); return; }
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/my-training/assessments/${current.id}/attempts`);
      setAttemptId(data.id);
    } catch (e) {
      setError(errorMessage(e, 'Não foi possível iniciar a avaliação'));
      load();
    } finally {
      setBusy(false);
    }
  };

  if (!course) {
    return (
      <div className="page-card">
        <button type="button" className="training-btn" onClick={onBack}><LuArrowLeft /> Voltar</button>
        <div className="training-hint" style={{ marginTop: 16 }}>{error || 'Carregando…'}</div>
      </div>
    );
  }

  if (attemptId) {
    return (
      <TrainingAttemptView
        attemptId={attemptId}
        onClose={() => { setAttemptId(null); load(); }}
      />
    );
  }

  const enrollment = course.enrollment;

  const navIcon = (item) => {
    const d = item.data;
    if (item.kind === 'lesson') {
      if (d.completed) return <LuCheckCircle2 color="var(--success)" />;
      if (!d.unlocked) return <LuLock color="var(--text-3)" />;
      return <LuCircle color="var(--text-3)" />;
    }
    if (d.passed) return <LuCheckCircle2 color="var(--success)" />;
    if (!d.unlocked) return <LuLock color="var(--text-3)" />;
    if (d.attempts_left === 0) return <LuXCircle color="var(--danger)" />;
    return <LuListChecks color="var(--text-3)" />;
  };

  return (
    <div className="page-card">
      <div className="training-editor-header">
        <div style={{ minWidth: 0 }}>
          <button type="button" className="training-btn training-btn--sm" onClick={onBack} style={{ marginBottom: 10 }}>
            <LuArrowLeft /> Meus treinamentos
          </button>
          <div className="training-editor-title">
            {course.title}
            {enrollment && <Badge meta={ENROLLMENT_STATUS[enrollment.status]} />}
          </div>
          <div className="training-row-meta" style={{ marginTop: 6 }}>
            {course.workload_hours !== null && <span><LuClock /> {Number(course.workload_hours).toLocaleString('pt-BR')} h</span>}
            <span>Nota mínima do curso: {course.passing_score}</span>
            {course.release?.due_date && <span>Prazo: {fmtDate(course.release.due_date)}</span>}
            {course.release?.mandatory && <span style={{ color: 'var(--warning)', fontWeight: 600 }}>Obrigatório</span>}
          </div>
        </div>
        {enrollment && (
          <div style={{ minWidth: 200, flex: '0 1 260px' }}>
            <div className="training-row-meta" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
              <span>Progresso</span><strong style={{ color: 'var(--text-1)' }}>{enrollment.progress_percent}%</strong>
            </div>
            <div className="training-progress" style={{ height: 10 }}><span style={{ width: `${enrollment.progress_percent}%` }} /></div>
          </div>
        )}
      </div>

      {error && <div className="alert-custom danger">{error}</div>}
      {course.read_only && <div className="alert-custom warning">Este curso foi arquivado: você pode consultar o conteúdo, mas não registrar novo progresso.</div>}

      {enrollment?.status === 'approved' && (
        <div className="training-result-banner is-approved">
          <LuAward size={30} />
          <div style={{ flex: 1 }}>
            <strong>Parabéns! Você foi aprovado neste curso.</strong>
            <div>{enrollment.final_score !== null ? `Nota final ${fmtScore(enrollment.final_score)} · ` : ''}Concluído em {fmtDate(enrollment.completed_at)}</div>
          </div>
          {course.certificate && (
            <button type="button" className="training-btn training-btn--dark" onClick={() => downloadCertificate(course)}>
              <LuAward /> Baixar certificado
            </button>
          )}
        </div>
      )}
      {enrollment?.status === 'failed' && (
        <div className="training-result-banner is-failed">
          <LuXCircle size={26} />
          <div>
            <strong>Você não foi aprovado neste curso.</strong>
            <div>As tentativas de uma avaliação se esgotaram{enrollment.final_score !== null ? ` · média ${fmtScore(enrollment.final_score)}` : ''}. Procure o responsável pelo treinamento.</div>
          </div>
        </div>
      )}

      {!enrollment && !course.read_only && (
        <div className="training-start-box">
          {course.description && <p style={{ margin: 0, color: 'var(--text-2)' }}>{course.description}</p>}
          <button type="button" className="training-btn training-btn--primary" onClick={handleStartCourse} disabled={busy}>
            <LuPlay /> {busy ? 'Iniciando…' : 'Começar curso'}
          </button>
        </div>
      )}

      <button type="button" className="training-btn training-player-nav-toggle" onClick={() => setNavOpen((v) => !v)}>
        <LuList /> {navOpen ? 'Fechar conteúdo do curso' : 'Ver conteúdo do curso'}
      </button>

      <div className="training-player">
        <nav className={`training-player-nav ${navOpen ? 'is-open' : ''}`}>
          {course.modules.map((m, mi) => (
            <div key={m.id}>
              <div className="training-preview-module">Módulo {mi + 1}: {m.title}</div>
              {[...m.lessons.map((l) => ({ kind: 'lesson', id: l.id, data: l })), ...m.assessments.map((a) => ({ kind: 'assessment', id: a.id, data: a }))].map((item) => (
                <button key={itemKey(item.kind, item.id)} type="button"
                  className={`training-player-item ${selected === itemKey(item.kind, item.id) ? 'is-active' : ''} ${item.data.unlocked ? '' : 'is-locked'}`}
                  onClick={() => go(item)}>
                  <span className="training-player-item-icon">{navIcon(item)}</span>
                  <span>{item.data.title}{item.kind === 'lesson' && !item.data.required ? ' (opcional)' : ''}</span>
                </button>
              ))}
            </div>
          ))}
          {course.final_assessments.length > 0 && (
            <div>
              <div className="training-preview-module">Prova final</div>
              {course.final_assessments.map((a) => {
                const item = { kind: 'assessment', id: a.id, data: a };
                return (
                  <button key={itemKey('assessment', a.id)} type="button"
                    className={`training-player-item ${selected === itemKey('assessment', a.id) ? 'is-active' : ''} ${a.unlocked ? '' : 'is-locked'}`}
                    onClick={() => go(item)}>
                    <span className="training-player-item-icon">{navIcon(item)}</span>
                    <span>{a.title}</span>
                  </button>
                );
              })}
            </div>
          )}
        </nav>

        <div className="training-player-main">
          {!current && <div className="training-hint">Este curso ainda não tem conteúdo.</div>}
          {current && !current.data.unlocked && current.kind === 'lesson' && (
            <div className="empty-state">
              <LuLock size={30} color="var(--text-3)" />
              <p>Aula bloqueada</p>
              <small>Este curso exige conclusão sequencial: conclua a etapa anterior para liberar esta aula.</small>
            </div>
          )}
          {current && current.kind === 'lesson' && current.data.unlocked && (
            <LessonContent lesson={current.data} course={course} onComplete={handleComplete} completing={busy} />
          )}
          {current && current.kind === 'assessment' && (
            <AssessmentPanel assessment={current.data} course={course} onStart={handleStartAttempt} starting={busy} />
          )}

          {current && (
            <div className="training-player-pager">
              <button type="button" className="training-btn" disabled={index <= 0} onClick={() => go(items[index - 1])}>
                <LuChevronLeft /> Anterior
              </button>
              <button type="button" className="training-btn" disabled={index >= items.length - 1} onClick={() => go(items[index + 1])}>
                Próximo <LuChevronRight />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
